import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PenLine } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Skeleton } from "@/components/ui/skeleton";
import { ArticleCard } from "@/components/articles/ArticleBody";

export default function AuthorPage() {
  const { slug } = useParams();
  const [author, setAuthor] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    api.get(`/authors/${slug}`).then((r) => setAuthor(r.data)).catch(() => setMissing(true));
  }, [slug]);
  useEffect(() => {
    if (!author) return;
    document.title = `${author.name} · Underdog Stories`;
    const url = `${window.location.origin}/authors/${author.slug}`;
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-author-ld", "1");
    ld.textContent = JSON.stringify({ "@context": "https://schema.org", "@type": "ProfilePage", mainEntity: { "@type": "Person", "@id": `${url}#person`, name: author.name, url, description: author.bio, image: author.photo_url || undefined, jobTitle: "Staff writer",
      worksFor: { "@type": "Organization", name: "Underdog Podcasts", url: window.location.origin } } });
    document.head.appendChild(ld);
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", url);
    return () => { ld.remove(); document.title = "Underdog Podcasts"; };
  }, [author]);
  return (
    <div className="min-h-screen flex flex-col" data-testid="author-page">
      <Navbar />
      <main className="flex-1 w-full max-w-[1400px] mx-auto px-5 sm:px-8 pt-10 pb-32">
        {missing && <p className="text-center text-muted-foreground py-20" data-testid="author-missing">No writer by that name here.</p>}
        {!author && !missing && <div className="space-y-4"><Skeleton className="h-24 w-24 rounded-full" /><Skeleton className="h-12 w-80" /><Skeleton className="h-6 w-2/3" /></div>}
        {author && (
          <>
            <header className="flex flex-col sm:flex-row sm:items-center gap-6 mb-12 reveal" data-testid="author-header">
              {author.photo_url ? <img src={author.photo_url} alt={author.name} className="h-28 w-28 rounded-full object-cover shrink-0" data-testid="author-photo" /> : <div className="h-28 w-28 rounded-full bg-sky-400/15 text-sky-300 grid place-items-center shrink-0"><PenLine size={40} /></div>}
              <div>
                <p className="eyebrow mb-2">Underdog Stories · Staff writer</p>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none" data-testid="author-name">{author.name}</h1>
                {author.bio && <p className="mt-4 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed" data-testid="author-bio">{author.bio}</p>}
                <p className="mt-3 text-sm text-muted-foreground" data-testid="author-stats">{author.articles.length} {author.articles.length === 1 ? "story" : "stories"} · {author.total_words.toLocaleString()} words</p>
              </div>
            </header>
            {author.articles.length === 0 ? <p className="text-muted-foreground" data-testid="author-empty">No stories published yet.</p>
              : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="author-articles">{author.articles.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}</div>}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
